import { useEffect, useRef, useState } from 'react'
import { GlyphIcon } from './GlyphIcon'

export const TOUR_SOUNDTRACK_VIDEO_ID = 'Xwz3UAErGrE'
export const TOUR_SOUNDTRACK_TITLE = 'I Kinda Like That'
export const TOUR_SOUNDTRACK_ARTIST = 'The Paradox'
export const TOUR_SOUNDTRACK_URL = `https://www.youtube.com/watch?v=${TOUR_SOUNDTRACK_VIDEO_ID}`

const YOUTUBE_API_SCRIPT_ID = 'youtube-iframe-api'
const YOUTUBE_API_SRC = 'https://www.youtube.com/iframe_api'

type PlaybackState = 'loading' | 'starting' | 'playing' | 'ready' | 'paused' | 'ended' | 'unavailable'

interface YouTubePlayerEvent {
  target: YouTubePlayer
}

interface YouTubeStateEvent extends YouTubePlayerEvent {
  data: number
}

interface YouTubePlayer {
  destroy: () => void
  getIframe: () => HTMLIFrameElement
  isMuted: () => boolean
  pauseVideo: () => void
  playVideo: () => void
  seekTo: (seconds: number, allowSeekAhead: boolean) => void
  setVolume: (volume: number) => void
  unMute: () => void
}

interface YouTubePlayerOptions {
  height: string
  host: string
  playerVars: Record<string, number | string>
  videoId: string
  width: string
  events: {
    onAutoplayBlocked: (event: YouTubePlayerEvent) => void
    onError: () => void
    onReady: (event: YouTubePlayerEvent) => void
    onStateChange: (event: YouTubeStateEvent) => void
  }
}

interface YouTubeApi {
  Player: new (element: HTMLElement, options: YouTubePlayerOptions) => YouTubePlayer
  PlayerState: {
    ENDED: number
    PLAYING: number
    PAUSED: number
  }
}

declare global {
  interface Window {
    YT?: YouTubeApi
    onYouTubeIframeAPIReady?: () => void
  }
}

let youtubeApiPromise: Promise<YouTubeApi> | undefined

function loadYouTubeApi(): Promise<YouTubeApi> {
  if (window.YT?.Player) return Promise.resolve(window.YT)
  if (youtubeApiPromise) return youtubeApiPromise

  youtubeApiPromise = new Promise<YouTubeApi>((resolve, reject) => {
    const priorReadyHandler = window.onYouTubeIframeAPIReady
    const timeout = window.setTimeout(() => reject(new Error('YouTube player API timed out.')), 15_000)

    window.onYouTubeIframeAPIReady = () => {
      priorReadyHandler?.()
      window.clearTimeout(timeout)
      if (window.YT?.Player) resolve(window.YT)
      else reject(new Error('YouTube player API was not available after loading.'))
    }

    const existingScript = document.getElementById(YOUTUBE_API_SCRIPT_ID)
    if (existingScript) return

    const script = document.createElement('script')
    script.id = YOUTUBE_API_SCRIPT_ID
    script.src = YOUTUBE_API_SRC
    script.async = true
    script.addEventListener('error', () => {
      window.clearTimeout(timeout)
      reject(new Error('YouTube player API failed to load.'))
    }, { once: true })
    document.head.append(script)
  })

  return youtubeApiPromise
}

const playbackStatusCopy: Record<PlaybackState, string> = {
  loading: `Loading ${TOUR_SOUNDTRACK_TITLE}.`,
  starting: `Starting ${TOUR_SOUNDTRACK_TITLE}.`,
  playing: `${TOUR_SOUNDTRACK_TITLE} is playing.`,
  ready: `${TOUR_SOUNDTRACK_TITLE} is ready at the beginning. Press play if your browser blocked autoplay.`,
  paused: `${TOUR_SOUNDTRACK_TITLE} is paused.`,
  ended: `${TOUR_SOUNDTRACK_TITLE} has finished.`,
  unavailable: `${TOUR_SOUNDTRACK_TITLE} could not load. Open it on YouTube instead.`,
}

function controlLabel(playbackState: PlaybackState): string {
  if (playbackState === 'ready') return `Play soundtrack from the beginning: ${TOUR_SOUNDTRACK_TITLE}`
  if (playbackState === 'paused') return `Play soundtrack: ${TOUR_SOUNDTRACK_TITLE}`
  if (playbackState === 'ended') return `Play soundtrack again: ${TOUR_SOUNDTRACK_TITLE}`
  if (playbackState === 'loading') return `Loading soundtrack: ${TOUR_SOUNDTRACK_TITLE}`
  return `Pause soundtrack: ${TOUR_SOUNDTRACK_TITLE}`
}

export function TourSoundtrack() {
  const playerHostRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<YouTubePlayer | null>(null)
  const autoplayBlockedRef = useRef(false)
  const hasStartedAudiblyRef = useRef(false)
  const [playbackState, setPlaybackState] = useState<PlaybackState>('loading')

  useEffect(() => {
    if (import.meta.env.MODE === 'test' && !window.YT?.Player) return

    let disposed = false
    let autoplayTimer = 0
    const host = playerHostRef.current
    if (!host) return

    setPlaybackState('loading')
    autoplayBlockedRef.current = false
    hasStartedAudiblyRef.current = false

    void loadYouTubeApi()
      .then((api) => {
        if (disposed) return
        const mount = document.createElement('div')
        host.replaceChildren(mount)

        const parkAtBeginning = (target: YouTubePlayer) => {
          autoplayBlockedRef.current = true
          window.clearTimeout(autoplayTimer)
          target.pauseVideo()
          target.seekTo(0, true)
          setPlaybackState('ready')
        }

        const player = new api.Player(mount, {
          width: '200',
          height: '200',
          host: 'https://www.youtube-nocookie.com',
          videoId: TOUR_SOUNDTRACK_VIDEO_ID,
          playerVars: {
            autoplay: 1,
            controls: 0,
            disablekb: 1,
            enablejsapi: 1,
            fs: 0,
            iv_load_policy: 3,
            origin: window.location.origin,
            playsinline: 1,
            rel: 0,
            start: 0,
          },
          events: {
            onReady: (event) => {
              if (disposed) return
              playerRef.current = event.target
              const iframe = event.target.getIframe()
              iframe.title = `${TOUR_SOUNDTRACK_ARTIST} — ${TOUR_SOUNDTRACK_TITLE} soundtrack player`
              iframe.tabIndex = -1
              iframe.setAttribute('aria-hidden', 'true')
              iframe.allow = 'autoplay; encrypted-media'
              event.target.seekTo(0, true)
              event.target.setVolume(65)
              event.target.unMute()
              setPlaybackState('starting')
              event.target.playVideo()

              autoplayTimer = window.setTimeout(() => {
                if (disposed || hasStartedAudiblyRef.current || autoplayBlockedRef.current) return
                parkAtBeginning(event.target)
              }, 2_000)
            },
            onAutoplayBlocked: (event) => {
              if (disposed || hasStartedAudiblyRef.current) return
              parkAtBeginning(event.target)
            },
            onStateChange: (event) => {
              if (disposed) return
              if (event.data === api.PlayerState.PLAYING) {
                if (autoplayBlockedRef.current || event.target.isMuted()) {
                  parkAtBeginning(event.target)
                  return
                }
                hasStartedAudiblyRef.current = true
                window.clearTimeout(autoplayTimer)
                setPlaybackState('playing')
              } else if (event.data === api.PlayerState.PAUSED) {
                setPlaybackState(autoplayBlockedRef.current ? 'ready' : 'paused')
              } else if (event.data === api.PlayerState.ENDED) {
                autoplayBlockedRef.current = false
                setPlaybackState('ended')
              }
            },
            onError: () => {
              window.clearTimeout(autoplayTimer)
              if (!disposed) setPlaybackState('unavailable')
            },
          },
        })
        playerRef.current = player
      })
      .catch(() => {
        if (!disposed) setPlaybackState('unavailable')
      })

    return () => {
      disposed = true
      window.clearTimeout(autoplayTimer)
      playerRef.current?.destroy()
      playerRef.current = null
      host.replaceChildren()
    }
  }, [])

  const togglePlayback = () => {
    const player = playerRef.current
    if (!player) return

    if (playbackState === 'ready') {
      autoplayBlockedRef.current = false
      hasStartedAudiblyRef.current = false
      player.seekTo(0, true)
      player.setVolume(65)
      player.unMute()
      player.playVideo()
      setPlaybackState('starting')
      return
    }

    if (playbackState === 'paused') {
      player.playVideo()
      setPlaybackState('starting')
      return
    }

    if (playbackState === 'ended') {
      player.seekTo(0, true)
      player.playVideo()
      setPlaybackState('starting')
      return
    }

    player.pauseVideo()
    setPlaybackState('paused')
  }

  const showPlayIcon = playbackState === 'ready' || playbackState === 'paused' || playbackState === 'ended'

  return (
    <div
      className="tour-soundtrack"
      data-playback-state={playbackState}
      data-tour-soundtrack={TOUR_SOUNDTRACK_VIDEO_ID}
    >
      {playbackState === 'unavailable' ? (
        <a
          aria-label={`Open soundtrack on YouTube: ${TOUR_SOUNDTRACK_TITLE}`}
          className="tour-soundtrack__control tour-soundtrack__fallback"
          href={TOUR_SOUNDTRACK_URL}
          rel="noopener noreferrer"
          target="_blank"
          title={`Open ${TOUR_SOUNDTRACK_TITLE} on YouTube`}
        >
          <GlyphIcon name="play" />
        </a>
      ) : (
        <button
          aria-label={controlLabel(playbackState)}
          className="tour-soundtrack__control"
          disabled={playbackState === 'loading'}
          onClick={togglePlayback}
          title={controlLabel(playbackState)}
          type="button"
        >
          <GlyphIcon name={showPlayIcon ? 'play' : 'pause'} />
        </button>
      )}

      <div aria-hidden="true" className="tour-soundtrack__player" ref={playerHostRef} />
      <span className="sr-only">{TOUR_SOUNDTRACK_ARTIST} — {TOUR_SOUNDTRACK_TITLE}</span>
      <span aria-atomic="true" aria-live="polite" className="sr-only">
        {playbackStatusCopy[playbackState]}
      </span>
    </div>
  )
}
